import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Tag } from 'lucide-react';
import { getAllPosts } from './blogPosts';
import { BlogPreviewCard } from './BlogPreviewCard';
import { SEOHead } from '../components/SEOHead';
import { useLocaleContext } from '../context/LocaleContext';
import { BLOG_CATEGORIES, type BlogCategoryId } from './categories';

export const BlogList: React.FC = () => {
  const { locale } = useLocaleContext();
  const [selectedCategory, setSelectedCategory] = useState<BlogCategoryId | 'all'>('all');

  const allPosts = getAllPosts(locale);

  // Filter posts by selected category
  const posts = selectedCategory === 'all'
    ? allPosts
    : allPosts.filter(post => post.category === selectedCategory);

  console.log('BlogList: Loaded posts:', posts.length, 'for locale:', locale);
  if (posts.length > 0) {
    console.log('BlogList: First post coverImage:', posts[0].coverImage);
  }

  useEffect(() => {
    // Guard against SSR - only run on client
    if (typeof window === 'undefined') return;

    // Add JSON-LD structured data for Blog
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      'name': 'AI Watermark Removal Blog',
      'description': 'Explore articles on AI watermark detection, invisible text cleaning, and document privacy. Learn how to remove hidden AI traces easily.',
      'url': 'https://gpt-watermark-remover.com/blog',
      'publisher': {
        '@type': 'Organization',
        'name': 'GROW FAST LTD.',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://gpt-watermark-remover.com/logo.png'
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <SEOHead
        title="AI Watermark Removal Blog | GPT Watermark Remover"
        description="Discover articles on AI watermarks, invisible text cleaning, and privacy. Learn how to detect and remove hidden AI traces from your writing."
      />

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded-full mb-6">
            <BookOpen className="w-5 h-5" aria-hidden="true" />
            <span className="font-semibold">Blog</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-charcoal-900 dark:text-white mb-6">
            {locale === 'de'
              ? 'Entdecken Sie, wie KI-Wasserzeichen funktionieren'
              : locale === 'pt-BR'
              ? 'Descubra Como Funcionam as Marcas d\'Água de IA'
              : locale === 'ms-MY'
              ? 'Ketahui Cara Tanda Air AI Berfungsi'
              : locale === 'es'
              ? 'Descubre Cómo Funcionan las Marcas de Agua de IA'
              : locale === 'fr'
              ? 'Découvrez le Fonctionnement des Filigranes IA'
              : locale === 'ja'
              ? 'AI透かしの仕組みを理解する'
              : locale === 'id'
              ? 'Pahami Cara Kerja Watermark AI'
              : 'Discover How AI Watermarks Work'}
          </h1>

          <p className="text-xl text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto mb-8">
            {locale === 'de'
              ? 'Lernen Sie alles über unsichtbare KI-Textwasserzeichen, Zero-Width-Spaces und wie Sie diese aus Ihren Dokumenten entfernen.'
              : locale === 'pt-BR'
              ? 'Aprenda tudo sobre marcas d\'água invisíveis de texto de IA, espaços de largura zero e como limpá-los de seus documentos.'
              : locale === 'ms-MY'
              ? 'Pelajari segala-galanya tentang tanda air teks AI yang tidak kelihatan, ruang lebar sifar, dan cara membersihkannya daripada dokumen anda.'
              : locale === 'es'
              ? 'Aprende todo sobre marcas de agua invisibles de texto de IA, espacios de ancho cero y cómo limpiarlos de tus documentos.'
              : locale === 'fr'
              ? 'Apprenez tout sur les filigranes de texte IA invisibles, les espaces de largeur nulle et comment les supprimer de vos documents.'
              : locale === 'ja'
              ? '不可視のAIテキスト透かし、ゼロ幅スペース、そしてドキュメントからそれらを削除する方法について学びましょう。'
              : locale === 'id'
              ? 'Pelajari semua tentang watermark teks AI yang tidak terlihat, spasi lebar-nol, dan cara menghapusnya dari dokumen Anda.'
              : 'Learn everything about invisible AI text watermarks, zero-width spaces, and how to clean them from your documents.'}
          </p>

          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl"
          >
            {locale === 'de'
              ? 'Tool testen'
              : locale === 'pt-BR'
              ? 'Experimentar a Ferramenta'
              : locale === 'ms-MY'
              ? 'Cuba Alat'
              : locale === 'es'
              ? 'Probar la Herramienta'
              : locale === 'fr'
              ? 'Essayer l\'Outil'
              : locale === 'ja'
              ? 'ツールを試す'
              : locale === 'id'
              ? 'Coba Alatnya'
              : 'Try the Tool'}
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Tag className="w-5 h-5 text-accent-600 dark:text-accent-400" aria-hidden="true" />
            <h2 className="text-xl font-bold text-charcoal-900 dark:text-white">
              {locale === 'de'
                ? 'Nach Kategorie filtern'
                : locale === 'pt-BR'
                ? 'Filtrar por Categoria'
                : locale === 'ms-MY'
                ? 'Tapis mengikut Kategori'
                : locale === 'es'
                ? 'Filtrar por Categoría'
                : locale === 'fr'
                ? 'Filtrer par Catégorie'
                : locale === 'ja'
                ? 'カテゴリで絞り込む'
                : locale === 'id'
                ? 'Filter berdasarkan Kategori'
                : 'Filter by Category'}
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              aria-pressed={selectedCategory === 'all'}
              aria-label={`Filter by all articles, ${allPosts.length} posts`}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-accent-600 text-white shadow-lg'
                  : 'bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500'
              }`}
            >
              {locale === 'de'
                ? `Alle Artikel (${allPosts.length})`
                : locale === 'pt-BR'
                ? `Todos os Artigos (${allPosts.length})`
                : locale === 'ms-MY'
                ? `Semua Artikel (${allPosts.length})`
                : locale === 'es'
                ? `Todos los Artículos (${allPosts.length})`
                : locale === 'fr'
                ? `Tous les Articles (${allPosts.length})`
                : locale === 'ja'
                ? `すべての記事 (${allPosts.length})`
                : locale === 'id'
                ? `Semua Artikel (${allPosts.length})`
                : `All Articles (${allPosts.length})`}
            </button>
            {BLOG_CATEGORIES.map((category) => {
              const categoryPosts = allPosts.filter(p => p.category === category.id);
              const categoryLabel = category.label[locale as keyof typeof category.label] || category.label.en;

              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  aria-pressed={selectedCategory === category.id}
                  aria-label={`Filter by ${categoryLabel}, ${categoryPosts.length} posts`}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === category.id
                      ? 'bg-accent-600 text-white shadow-lg'
                      : 'bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500'
                  }`}
                >
                  {categoryLabel} ({categoryPosts.length})
                </button>
              );
            })}
          </div>
        </div>

        {/* Blog Posts Grid */}
        {posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {posts.map((post) => (
              <BlogPreviewCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 mx-auto text-neutral-400 dark:text-neutral-600 mb-4" aria-hidden="true" />
            <h2 className="text-2xl font-bold text-neutral-600 dark:text-neutral-400 mb-2">
              {locale === 'de'
                ? 'Noch keine Blog-Beiträge'
                : locale === 'pt-BR'
                ? 'Nenhuma postagem ainda'
                : locale === 'ms-MY'
                ? 'Belum ada artikel blog'
                : locale === 'es'
                ? 'Aún no hay publicaciones'
                : locale === 'fr'
                ? 'Aucun article pour le moment'
                : locale === 'ja'
                ? 'まだ記事がありません'
                : locale === 'id'
                ? 'Belum ada artikel blog'
                : 'No blog posts yet'}
            </h2>
            <p className="text-neutral-500 dark:text-neutral-500">
              {locale === 'de'
                ? 'Schauen Sie bald wieder vorbei für Artikel über KI-Wasserzeichen und Textbereinigung.'
                : locale === 'pt-BR'
                ? 'Volte em breve para artigos sobre marcas d\'água de IA e limpeza de texto.'
                : locale === 'ms-MY'
                ? 'Kembali semula tidak lama lagi untuk artikel tentang tanda air AI dan pembersihan teks.'
                : locale === 'es'
                ? 'Vuelve pronto para artículos sobre marcas de agua de IA y limpieza de texto.'
                : locale === 'fr'
                ? 'Revenez bientôt pour des articles sur les filigranes IA et le nettoyage de texte.'
                : locale === 'ja'
                ? 'AI透かしとテキストクリーニングに関する記事を近日公開予定です。'
                : locale === 'id'
                ? 'Kunjungi lagi nanti untuk artikel tentang watermark AI dan pembersihan teks.'
                : 'Check back soon for articles about AI watermarks and text cleaning.'}
            </p>
          </div>
        )}

        {/* SEO Content Section */}
        <div className="bg-neutral-100 dark:bg-charcoal-800/50 rounded-xl p-8 border border-neutral-200 dark:border-charcoal-700">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-4">
            {locale === 'de'
              ? 'Über diesen Blog'
              : locale === 'pt-BR'
              ? 'Sobre Este Blog'
              : locale === 'ms-MY'
              ? 'Tentang Blog Ini'
              : locale === 'es'
              ? 'Acerca de Este Blog'
              : locale === 'fr'
              ? 'À Propos de Ce Blog'
              : locale === 'ja'
              ? 'このブログについて'
              : locale === 'id'
              ? 'Tentang Blog Ini'
              : 'About This Blog'}
          </h2>
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-neutral-600 dark:text-neutral-300">
              {locale === 'de'
                ? 'Willkommen beim AI Watermark Removal Blog. Hier finden Sie umfassende Leitfäden, Tutorials und Einblicke zu:'
                : locale === 'pt-BR'
                ? 'Bem-vindo ao Blog de Remoção de Marcas d\'Água de IA. Aqui você encontrará guias abrangentes, tutoriais e insights sobre:'
                : locale === 'ms-MY'
                ? 'Selamat datang ke Blog Penyingkiran Tanda Air AI. Di sini anda akan menemui panduan menyeluruh, tutorial, dan pandangan tentang:'
                : locale === 'es'
                ? 'Bienvenido al Blog de Eliminación de Marcas de Agua de IA. Aquí encontrarás guías completas, tutoriales y conocimientos sobre:'
                : locale === 'fr'
                ? 'Bienvenue sur le Blog de Suppression de Filigranes IA. Vous y trouverez des guides complets, des tutoriels et des informations sur :'
                : locale === 'ja'
                ? 'AI透かし削除ブログへようこそ。こちらでは以下に関する包括的なガイド、チュートリアル、解説をご覧いただけます：'
                : locale === 'id'
                ? 'Selamat datang di Blog Penghapusan Watermark AI. Di sini Anda akan menemukan panduan lengkap, tutorial, dan wawasan tentang:'
                : 'Welcome to the AI Watermark Removal Blog. Here you\'ll find comprehensive guides, tutorials, and insights about:'}
            </p>
            <ul className="text-neutral-600 dark:text-neutral-300 space-y-2">
              <li><strong>{locale === 'de' ? 'KI-Wasserzeichen:' : locale === 'pt-BR' ? 'Marcas d\'Água de IA:' : locale === 'ms-MY' ? 'Tanda Air AI:' : locale === 'es' ? 'Marcas de Agua de IA:' : locale === 'fr' ? 'Filigranes IA :' : locale === 'ja' ? 'AI透かし：' : locale === 'id' ? 'Watermark AI:' : 'AI Watermarks:'}</strong> {locale === 'de' ? 'Wie sie funktionieren und warum sie wichtig sind' : locale === 'pt-BR' ? 'Como funcionam e por que são importantes' : locale === 'ms-MY' ? 'Bagaimana ia berfungsi dan mengapa ia penting' : locale === 'es' ? 'Cómo funcionan y por qué son importantes' : locale === 'fr' ? 'Leur fonctionnement et leur importance' : locale === 'ja' ? '仕組みと重要性' : locale === 'id' ? 'Cara kerjanya dan mengapa penting' : 'How they work and why they matter'}</li>
              <li><strong>{locale === 'de' ? 'Textbereinigung:' : locale === 'pt-BR' ? 'Limpeza de Texto:' : locale === 'ms-MY' ? 'Pembersihan Teks:' : locale === 'es' ? 'Limpieza de Texto:' : locale === 'fr' ? 'Nettoyage de Texte :' : locale === 'ja' ? 'テキストクリーニング：' : locale === 'id' ? 'Pembersihan Teks:' : 'Text Cleaning:'}</strong> {locale === 'de' ? 'Unsichtbare Zeichen aus Dokumenten entfernen' : locale === 'pt-BR' ? 'Removendo caracteres invisíveis de documentos' : locale === 'ms-MY' ? 'Membuang aksara halimunan daripada dokumen' : locale === 'es' ? 'Eliminar caracteres invisibles de documentos' : locale === 'fr' ? 'Supprimer les caractères invisibles des documents' : locale === 'ja' ? 'ドキュメントから不可視文字を削除' : locale === 'id' ? 'Menghapus karakter tersembunyi dari dokumen' : 'Removing invisible characters from documents'}</li>
              <li><strong>{locale === 'de' ? 'Datenschutz:' : locale === 'pt-BR' ? 'Privacidade:' : locale === 'ms-MY' ? 'Privasi:' : locale === 'es' ? 'Privacidad:' : locale === 'fr' ? 'Confidentialité :' : locale === 'ja' ? 'プライバシー：' : locale === 'id' ? 'Privasi:' : 'Privacy:'}</strong> {locale === 'de' ? 'Schützen Sie Ihre Inhalte vor KI-Erkennung' : locale === 'pt-BR' ? 'Protegendo seu conteúdo da detecção de IA' : locale === 'ms-MY' ? 'Melindungi kandungan anda daripada pengesanan AI' : locale === 'es' ? 'Proteger tu contenido de la detección de IA' : locale === 'fr' ? 'Protéger votre contenu contre la détection IA' : locale === 'ja' ? 'AI検出からコンテンツを保護' : locale === 'id' ? 'Melindungi konten Anda dari deteksi AI' : 'Protecting your content from AI detection'}</li>
              <li><strong>{locale === 'de' ? 'Dokumentformatierung:' : locale === 'pt-BR' ? 'Formatação de Documentos:' : locale === 'ms-MY' ? 'Pemformatan Dokumen:' : locale === 'es' ? 'Formato de Documentos:' : locale === 'fr' ? 'Mise en Forme des Documents :' : locale === 'ja' ? 'ドキュメントの書式設定：' : locale === 'id' ? 'Pemformatan Dokumen:' : 'Document Formatting:'}</strong> {locale === 'de' ? 'Probleme in Word, Pages und anderen Formaten beheben' : locale === 'pt-BR' ? 'Corrigindo problemas no Word, Pages e outros formatos' : locale === 'ms-MY' ? 'Membetulkan isu dalam Word, Pages, dan format lain' : locale === 'es' ? 'Solucionar problemas en Word, Pages y otros formatos' : locale === 'fr' ? 'Résoudre les problèmes dans Word, Pages et autres formats' : locale === 'ja' ? 'Word、Pages、その他の形式の問題を修正' : locale === 'id' ? 'Memperbaiki masalah di Word, Pages, dan format lainnya' : 'Fixing issues in Word, Pages, and other formats'}</li>
              <li><strong>{locale === 'de' ? 'Best Practices:' : locale === 'pt-BR' ? 'Melhores Práticas:' : locale === 'ms-MY' ? 'Amalan Terbaik:' : locale === 'es' ? 'Mejores Prácticas:' : locale === 'fr' ? 'Bonnes Pratiques :' : locale === 'ja' ? 'ベストプラクティス：' : locale === 'id' ? 'Praktik Terbaik:' : 'Best Practices:'}</strong> {locale === 'de' ? 'KI-Tools verantwortungsvoll und ethisch nutzen' : locale === 'pt-BR' ? 'Usando ferramentas de IA de forma responsável e ética' : locale === 'ms-MY' ? 'Menggunakan alat AI secara bertanggungjawab dan beretika' : locale === 'es' ? 'Usar herramientas de IA de manera responsable y ética' : locale === 'fr' ? 'Utiliser les outils IA de manière responsable et éthique' : locale === 'ja' ? 'AIツールを責任を持って倫理的に使用' : locale === 'id' ? 'Menggunakan alat AI secara bertanggung jawab dan etis' : 'Using AI tools responsibly and ethically'}</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogList;
